import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui';
import { Card } from './ui/Card';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, EyeIcon } from '../icons';

const ITEMS_PER_PAGE = 10;

const VolunteerTable = ({
  data,
  meta,
  training = false,
  lead_allocation = false,
  allocation = false,
  status = false,
  conflicts = true,
  select = true,
  selected = [],
  onSelectionChange = null,
  onConflictSelect = null,
  viewShiftsClick = null,
  disablePagination = false,
  onProfileClick = null,
  onConflictClick = null
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const columns = meta.columns || [];

  const totalPages = Math.ceil((data?.length || 0) / ITEMS_PER_PAGE);
  const effectivePage = Math.min(currentPage, totalPages);

  // Use paginatedData only if pagination is enabled, otherwise use all data
  const paginatedData = useMemo(() => {
    if (disablePagination) {
      return data;
    }
    const startIndex = (effectivePage - 1) * ITEMS_PER_PAGE;
    return data?.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [data, effectivePage, disablePagination]);


  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };


  // Logic for checking if a volunteer is already selected
  const isSelected = (id) => selected.some(v => v.csid === id);

  const toggleRow = (vol) => {
    if (!onSelectionChange) return;

    // remove volunteer is they are unchecked
    if (isSelected(vol.csid)) {
      onSelectionChange(selected.filter(v => v.csid !== vol.csid));

    }
    // add volunteer if they are checked
    else {
      onSelectionChange([...selected, vol]);
    }
  };

  const toggleSelection = (vol) => {
    onSelectionChange([vol]);
  }

  const allSelected = data?.length > 0 && data?.every(v => isSelected(v.csid));

  const toggleSelectAll = () => {
    if (!onSelectionChange) return;

    if (allSelected) {
      // Deselect everyone
      onSelectionChange([]);
    } else {
      // Select ALL volunteers in the entire dataset
      onSelectionChange([...data]);
    }
  };




  const calculateAge = (volunteer) => {
    // Try to calculate from DOB first
    if (volunteer?.dob) {
      const dob = new Date(volunteer.dob);
      if (!isNaN(dob.getTime())) {
        const today = new Date();
        let years = today.getFullYear() - dob.getFullYear();

        if (
          today.getMonth() < dob.getMonth() ||
          (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
        ) {
          years--;
        }

        if (years >= 0) return years;
      }
    }

    // Fallback to age field if DOB is not available
    if (volunteer?.age !== null && volunteer?.age !== undefined) {
      return volunteer.age;
    }

    return "N/A";
  };

  const Tag = ({ value = null, children }) => {

    const variantClasses: Record<string, string> = {
      Accepted: "bg-success-600 text-white",
      Pending: "bg-warn-400 text-gray-700",
      default: "bg-primary-600 text-white",
    };

    const classes = variantClasses[value] ?? variantClasses.default;
    return (
      <span className={`
        inline-flex items-center justify-center
        px-3 py-1 rounded-full ${classes}
        text-xs font-medium whitespace-nowrap`}>
        {children}
      </span>
    )
  }



  const renderCell = (item, column) => {
    const value = item[column.key];

    switch (column.key) {
      case "age":
        return calculateAge(item);


      case "dob":
        return item.dob || "N/A";

      case "ismaili":
      case "multi_faith_family":
      case "eligible_for_duty":
        return value ? "Yes" : "No";

      case "uk_didar_registration_ack":
        return value ? "Yes" : "No";

      case "languages": {
        if (!value) return "N/A";
        const langs = value.split(",").map(l => l.trim()).filter(Boolean);
        return (
          <div className="flex flex-wrap gap-2">
            {langs.map((lang, idx) => (
              <Tag key={idx}>{lang}</Tag>
            ))}
          </div>
        );
      }

      case "preferences":
        return (
          <div className="flex flex-wrap gap-2">
            {item.preferences?.length
              ? item.preferences.map(p => <Tag key={p.id}>{p.name}</Tag>)
              : "N/A"}
          </div>
        );

      case "flags":
        return (
          <div className="flex flex-wrap gap-2">
            {item.flags?.length
              ? item.flags.map(f => <Tag key={f.id}>{f.name}</Tag>)
              : "N/A"}
          </div>
        );

      case "support_needs":
        return value ? (
          <span className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2" title={value}>
            {value}
          </span>
        ) : "N/A";

      case "action":
        return (
          <div className="flex gap-3">
            <Button type='button' color='primary' size='sm' onClick={() => viewShiftsClick(item?.allocations, item)}>View Shifts</Button>
            <Button type='button' color='primary' size='sm' onClick={() => onProfileClick(item)}>View Profile</Button>
            {item?.conflicts?.length > 0 && <Button type='button' color='primary' size='sm' onClick={() => onConflictClick(item)}>View Conflicts</Button>}
          </div>
        )

      default:
        return value ?? "N/A";
    }
  };


  return (
    <>
      <Card>
        <div className="overflow-x-auto rounded-lg border border-gray-600">
          <table className="w-full text-start border-collapse table-auto dark:text-gray-300">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>

                {training && select && (
                  <th className="p-3 border border-gray-600 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}

                {lead_allocation && select && (
                  <th className="p-3 border border-gray-600">
                    {/* Empty header  */}
                  </th>
                )}


                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="text-sm font-semibold text-gray-700 dark:text-gray-200 p-3 border border-gray-600 whitespace-nowrap"
                  >
                    {column.header}
                  </th>
                ))}

                {allocation && status && (
                  <th className="text-sm font-semibold p-3 border border-gray-600 whitespace-nowrap dark:text-gray-200">
                    Status
                  </th>
                )}

                {allocation && conflicts && (
                  <th className="text-sm font-semibold p-3 border border-gray-600 whitespace-nowrap dark:text-gray-200">
                    Conflicts
                  </th>
                )}

                {!training && !lead_allocation && (
                  <th className="text-sm font-semibold p-3 border border-gray-600 whitespace-nowrap">
                    Actions
                  </th>
                )}



              </tr>
            </thead>

            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  < tr key={item.csid}
                    className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700"
                    onDoubleClick={() => lead_allocation ? toggleSelection(item) : toggleRow(item)}
                  >
                    {training && select && (
                      <td className="p-2 border border-gray-600 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected(item.csid)}
                          onChange={() => toggleRow(item)}
                        />
                      </td>
                    )}

                    {lead_allocation && select && (
                      <td className="p-2 border border-gray-600 text-center">
                        <input
                          type="radio"
                          checked={isSelected(item.csid)}
                          onChange={() => toggleSelection(item)}
                        />
                      </td>
                    )}

                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="text-sm p-2 border border-gray-600 align-top"
                      >
                        {renderCell(item, column)}
                      </td>
                    ))}

                    {allocation && status && (
                      <td className="p-2 border border-gray-600 whitespace-nowrap text-center">
                        <Tag value={item?.allocation?.status !== true ? "Pending" : "Accepted"}>{item?.allocation?.status !== true ? "Pending" : "Accepted"}</Tag>
                      </td>
                    )}

                    {allocation && conflicts && (
                      <td className="p-2 border border-gray-600 whitespace-nowrap text-center">
                        {item.conflicts && item.conflicts.length > 0 ? (
                          <Button type='button' color="primary" size="sm" onClick={() => onConflictSelect(item.conflicts)}>
                            View Conflicts
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">No Conflicts</span>
                        )}
                      </td>
                    )}

                    {!training && !lead_allocation && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm border-t-2 border-b-2 space-y-6">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onProfileClick && onProfileClick(item)
                          }}
                          className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline font-medium inline-flex items-center gap-1"
                        >
                          <EyeIcon className="w-4 h-4" />
                          View Profile
                        </button>

                        <Link
                          to={`/volunteer/${item.csid}/profile`}
                          className="flex text-primary-600 hover:text-primary-800 dark:text-primary-400"
                        >
                          <span className='flex'>
                            <EditIcon className="w-4 h-4 me-2" />
                            Edit Profile
                          </span>
                        </Link>
                      </td>

                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length + (training ? 1 : 1)}
                    className="text-center p-4 font-semibold text-gray-600 dark:text-gray-300 border border-gray-600"
                  >
                    No results match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!disablePagination && totalPages > 1 && (
          <div className="flex justify-end px-6 py-4 gap-2 items-center">
            <Button
              onClick={() => handlePageChange(1)}
              disabled={effectivePage === 1}
              size='sm'
              type='button'
            >
              <ChevronLeftIcon className='-me-[18px]' /><ChevronLeftIcon /> First
            </Button>

            <Button
              type='button'
              onClick={() => handlePageChange(effectivePage - 1)}
              disabled={effectivePage === 1}
              size='sm'
            >
              <ChevronLeftIcon /> Previous
            </Button>

            {(() => {
              const pageNumbers = [];
              const maxVisible = 5;
              let startPage = Math.max(1, effectivePage - Math.floor(maxVisible / 2));
              let endPage = Math.min(totalPages, startPage + maxVisible - 1);

              // Adjust start if we're near the end
              if (endPage - startPage < maxVisible - 1) {
                startPage = Math.max(1, endPage - maxVisible + 1);
              }

              // Show first page + ellipsis if needed
              if (startPage > 1) {
                pageNumbers.push(
                  <Button
                    key={1}
                    type='button'
                    onClick={() => handlePageChange(1)}
                    size='sm'
                    variant='icon'
                  >
                    1
                  </Button>
                );
                if (startPage > 2) {
                  pageNumbers.push(<span key="ellipsis1" className="px-2 text-gray-500">...</span>);
                }
              }

              // Show page numbers
              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(
                  <Button
                    key={i}
                    type='button'
                    onClick={() => handlePageChange(i)}
                    size='sm'
                    variant={i === effectivePage ? 'solid' : 'icon'}
                  >
                    {i}
                  </Button>
                );
              }

              // Show ellipsis + last page if needed
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pageNumbers.push(<span key="ellipsis2" className="px-2 text-gray-500">...</span>);
                }
                pageNumbers.push(
                  <Button
                    key={totalPages}
                    type='button'
                    onClick={() => handlePageChange(totalPages)}
                    size='sm'
                    variant='icon'
                  >
                    {totalPages}
                  </Button>
                );
              }

              return pageNumbers;
            })()}

            <Button
              type='button'
              onClick={() => handlePageChange(effectivePage + 1)}
              disabled={effectivePage >= totalPages}
              size='sm'
            >
              Next <ChevronRightIcon />
            </Button>

            <Button
              type='button'
              onClick={() => handlePageChange(totalPages)}
              disabled={effectivePage >= totalPages}
              size='sm'
            >
              Last <ChevronRightIcon /><ChevronRightIcon className='-ms-[18px]' />
            </Button>

            {data.length > 0 && (
              <span className="ms-4 text-sm text-gray-600 dark:text-gray-400">
                Showing {(effectivePage - 1) * ITEMS_PER_PAGE + 1}-
                {Math.min(effectivePage * ITEMS_PER_PAGE, data.length)} of {data.length} results
              </span>
            )}

            {data.length === 0 && (
              <span className="ms-4 text-sm text-gray-600 dark:text-gray-400">
                Showing 0 results
              </span>
            )}
          </div>
        )}
      </Card >

    </>
  );
};

export default VolunteerTable;
